import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loading, EmptyState } from "@/components/common/Loading";

// Otter Assist admin — Active / Historical Assists.
// Active vs Historical comes from the canonical lifecycle helpers on the server, never from a
// hard-coded status list here.

const URGENCY = ["immediate", "asap", "soon", "within_days", "flexible"];
const SORTS = [["recent", "Newest"], ["oldest", "Oldest"], ["deadline", "Deadline"],
               ["urgency", "Urgency"]];

export function OaAssists({ group }) {
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");
  const [urgency, setUrgency] = useState("all");
  const [sort, setSort] = useState("recent");
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ["oa-admin-assists", group, term, urgency, sort, page],
    queryFn: async () => (await api.get("/admin/otter-assist/assists", {
      params: { group, q: term || undefined, urgency: urgency === "all" ? undefined : urgency,
                sort, page, page_size: 25 },
    })).data,
  });

  const submit = (e) => { e.preventDefault(); setPage(1); setTerm(q.trim()); };
  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div className="space-y-4" data-testid={`oa-admin-assists-${group}`}>
      <form onSubmit={submit} className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[240px] flex-1">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="h-9 pl-8 text-[13px]"
            placeholder="Assist ID, post ID, sender email or need text"
            data-testid="oa-assists-search-input" />
        </div>
        <Select value={urgency} onValueChange={(v) => { setUrgency(v); setPage(1); }}>
          <SelectTrigger className="h-9 w-[170px] text-[13px]" data-testid="oa-assists-urgency">
            <SelectValue placeholder="Any urgency" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any urgency</SelectItem>
            {URGENCY.map((u) => <SelectItem key={u} value={u}>{u.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={(v) => { setSort(v); setPage(1); }}>
          <SelectTrigger className="h-9 w-[150px] text-[13px]" data-testid="oa-assists-sort">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button type="submit" size="sm" className="h-9" data-testid="oa-assists-search-submit">
          Search
        </Button>
      </form>

      {isLoading ? <Loading /> : !data?.items?.length ? (
        <EmptyState icon={Search} title="No Assists found"
          description="Try a different search or filter." testId="oa-assists-empty" />
      ) : (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Need</TableHead>
                <TableHead>Sender</TableHead>
                <TableHead>Urgency</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Helpers</TableHead>
                <TableHead className="text-right">Delivered</TableHead>
                <TableHead className="text-right">Reports</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((row) => (
                <TableRow key={row.assist_id} data-testid={`oa-assist-row-${row.assist_id}`}>
                  <TableCell className="max-w-[320px]">
                    <p className="truncate text-[13px]">{row.summary || "—"}</p>
                    <p className="font-mono text-[10px] text-muted-foreground">{row.assist_id}</p>
                  </TableCell>
                  <TableCell className="text-[12px]">
                    {row.sender?.name || "—"}
                    <span className="block text-[10px] text-muted-foreground">
                      {row.sender?.email}
                    </span>
                  </TableCell>
                  <TableCell className="text-[12px]">{row.urgency}</TableCell>
                  <TableCell>
                    <Badge variant="secondary" data-testid={`oa-assist-status-${row.assist_id}`}>
                      {row.status}
                    </Badge>
                    <span className="mt-0.5 block text-[10px] text-muted-foreground">
                      member sees: {row.public_status}
                    </span>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{row.responder_count}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.delivered_count}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.report_count ? (
                      <Badge variant="destructive">{row.report_count}</Badge>
                    ) : "0"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button asChild size="sm" variant="outline" className="h-7 text-[11px]">
                      <Link to={`/dashboard/admin/otter-assist/assist/${row.assist_id}`}
                        data-testid={`oa-assist-open-${row.assist_id}`}>Inspect</Link>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {data && data.total > data.page_size && (
        <div className="flex items-center justify-between text-[12px] text-muted-foreground">
          <span data-testid="oa-assists-total">{data.total} Assists</span>
          <span className="flex items-center gap-1.5">
            <Button size="sm" variant="outline" className="h-7" disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)} data-testid="oa-assists-prev">
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <span>{page} / {pages}</span>
            <Button size="sm" variant="outline" className="h-7" disabled={page >= pages}
              onClick={() => setPage((p) => p + 1)} data-testid="oa-assists-next">
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </span>
        </div>
      )}
    </div>
  );
}

export default OaAssists;
